import type { FormConfig } from './types'

// PLCC "New Project Brief" contact form. Embedded as an iframe on
// plcc-gmbh.de/kontakt (de) and plcc-gmbh.de/contact (en), replacing the
// previous simple Name/E-Mail/Message form. Backend routes formType "plcc"
// from info@plcc-gmbh.de to the PLCC_EMAIL_ADDRESS recipient (info@plcc.de).
// Two labeled sections on one page: project description, then formula/packaging.
export const plccForm: FormConfig = {
  id: 'plcc',
  submitLabel: { de: 'Abschicken', en: 'Send' },
  successMessage: {
    de: 'Vielen Dank, Ihre Anfrage wurde erfolgreich versendet.',
    en: 'Thank you, your request has been sent successfully.',
  },
  fields: [
    {
      id: 'sectionProject',
      type: 'section',
      label: { de: 'Projektbeschreibung', en: 'New Project Brief' },
    },
    {
      id: 'clientName',
      type: 'text',
      label: { de: 'Name des Kunden', en: 'Name of Client' },
      required: true,
    },
    {
      id: 'email',
      type: 'email',
      label: { de: 'E-Mail-Adresse', en: 'E-Mail Address' },
      required: true,
    },
    {
      id: 'phone',
      type: 'text',
      label: { de: 'Telefonnummer', en: 'Phone' },
    },
    {
      id: 'projectName',
      type: 'text',
      label: { de: 'Projektname', en: 'Name of Project' },
      required: true,
    },
    {
      id: 'projectType',
      type: 'select',
      label: { de: 'Projektart', en: 'Type of Project' },
      options: [
        { value: 'Make-up', label: { de: 'Make-up', en: 'Make-up' } },
        { value: 'Skin Care', label: { de: 'Skin Care', en: 'Skin Care' } },
        { value: 'Sonstiges', label: { de: 'Sonstiges', en: 'Others' } },
      ],
    },
    {
      id: 'skuCount',
      type: 'text',
      label: { de: 'Anzahl der SKUs', en: 'Number of SKU' },
    },
    {
      id: 'quantity',
      type: 'select',
      label: { de: 'Menge', en: 'Quantity' },
      required: true,
      options: [
        { value: '3.000', label: { de: '3.000', en: '3,000' } },
        { value: '5.000', label: { de: '5.000', en: '5,000' } },
        { value: '7.500', label: { de: '7.500', en: '7,500' } },
        { value: '10.000', label: { de: '10.000', en: '10,000' } },
        { value: '20.000', label: { de: '20.000', en: '20,000' } },
        { value: '25.000', label: { de: '25.000', en: '25,000' } },
        { value: 'MOQ', label: { de: 'MOQ', en: 'MOQ' } },
        { value: 'Sonstiges', label: { de: 'Sonstiges', en: 'Others' } },
      ],
    },
    {
      id: 'quantityOther',
      type: 'text',
      label: { de: 'Menge (Sonstiges)', en: 'Quantity (Others)' },
      dependsOn: { fieldId: 'quantity', equals: 'Sonstiges' },
    },
    {
      id: 'totalQuantity',
      type: 'text',
      label: { de: 'Gesamtmenge', en: 'Total Quantity' },
      description: {
        de: 'Summe aus Anzahl SKUs und Menge.',
        en: 'Sum of number of SKUs and quantity.',
      },
    },
    {
      id: 'netContent',
      type: 'text',
      label: { de: 'Gewünschter Nettoinhalt', en: 'Requested net content' },
    },
    {
      id: 'dispatchDate',
      type: 'text',
      label: { de: 'Gewünschtes Versanddatum', en: 'Requested date of dispatch' },
    },
    {
      id: 'launchDate',
      type: 'text',
      label: { de: 'Startdatum', en: 'Date of Launch' },
    },
    {
      id: 'serviceType',
      type: 'select',
      label: { de: 'Art der Dienstleistung', en: 'Type of Service' },
      options: [
        { value: 'Bulk', label: { de: 'Bulk', en: 'Bulk' } },
        {
          value: 'Halbfertiges Produkt',
          label: { de: 'Halbfertiges Produkt', en: 'Semifinished Product' },
        },
        {
          value: 'Verpackung vom Kunden bereitgestellt',
          label: { de: 'Verpackung vom Kunden bereitgestellt', en: 'Packaging from customer' },
        },
        { value: 'Komplettservice', label: { de: 'Komplettservice', en: 'Full Service' } },
      ],
    },
    {
      id: 'targetCosts',
      type: 'text',
      label: { de: 'Zielkosten (EUR)', en: 'Target Costs (EUR)' },
    },
    {
      id: 'sectionFormula',
      type: 'section',
      label: { de: 'Formulierung', en: 'Formula' },
    },
    {
      id: 'formulaFunction',
      type: 'textarea',
      label: { de: 'Formulierung und Produktfunktion', en: 'Formula & Product Function' },
      description: {
        de: 'Bitte versuchen Sie zu erläutern, nach welcher Art von Produkt Sie suchen (Lidschatten, Gesichtscreme usw.); die kosmetische Darreichungsform (flüssig, Puder, Stift usw.); ob Sie präzise Wirkversprechen formulieren möchten, ob Sie bestimmte Wirkstoffe, Duftstoffe usw. wünschen. Bitte geben Sie außerdem an, ob Sie die Wirkstoffe in Marketing- oder Funktionsdosierung wünschen; bei Kompaktpudern geben Sie bitte an, ob Sie ein flaches, geprägtes oder vertieftes Relief bzw. einen Spezialeffekt wünschen; bitte fügen Sie so viele Details wie möglich hinzu.',
        en: 'Please try to explain which type of product you are looking for (eyeshadow, face cream, etc); the cosmetic form (liquid, powder, stick, etc); if you want to make precise claims, if you want some specific actives, fragrance, etc. And please advise if you want the actives in marketing or functional dose; in case of compact powders please specify if you want a flat / embossed or debossed relief / special effect; please add as many details as possible.',
      },
    },
    {
      id: 'benchmarks',
      type: 'textarea',
      label: { de: 'Referenzwerte', en: 'Benchmarks' },
      description: {
        de: 'Bitte geben Sie die Referenzwerte für die Rezeptur und/oder die Farben an.',
        en: 'Please indicate the benchmarks for formula and/or colors.',
      },
    },
    {
      id: 'sectionPackaging',
      type: 'section',
      label: { de: 'Verpackung', en: 'Packaging' },
      description: {
        de: 'Es ist für uns sehr wichtig, Ihr Logo so schnell wie möglich in hoher Auflösung zu erhalten. Falls Sie über einen Styleguide oder ein Markenhandbuch für die Unternehmens- und/oder Produktkommunikation verfügen, teilen Sie uns dies bitte mit.',
        en: "It's very important for us to have your logo in high resolution as soon as possible. If you have a guideline or brand book for institutional and/or product communication, please inform us.",
      },
    },
    {
      id: 'primaryPackaging',
      type: 'textarea',
      label: { de: 'Primärverpackung', en: 'Primary Packaging' },
      description: {
        de: 'Bitte geben Sie an, welche Art von Verpackung Sie für das Endprodukt wünschen. Zum Beispiel bei einer Foundation-Flasche: Glas oder Kunststoff / Fassungsvermögen / Form / Oberfläche (klar/matt) und Dekoration (SS, HS, Inkjet, Lack); Zubehör (Pumpe / Tropfer), Verschluss (Form, Material, Farbe, Oberfläche).',
        en: 'Please indicate which type of packaging you would like to have in the finished product. For example in case of foundation-bottle: glass or plastic / capacity / shape / finish (flint/frosted) and decoration (SS, HS, inkjet, varnish); accessories (pump / dropper), cap (shape, material, color, finish).',
      },
    },
    {
      id: 'secondaryPackaging',
      type: 'textarea',
      label: { de: 'Sekundärverpackung', en: 'Secondary Packaging' },
      description: {
        de: 'Welche Art von Faltschachtel bevorzugen Sie? In welcher Oberfläche und Farbe? Bevorzugen Sie eine Sleeve oder ein Siegeletikett? Bitte geben Sie so viele Details wie möglich an.',
        en: 'Which kind of folding box do you prefer? In which finish and color? Do you prefer a sleeve or sealing label? Please try to give as many details as possible.',
      },
    },
    {
      id: 'additionalInfo',
      type: 'textarea',
      label: { de: 'Weitere Informationen', en: 'Additional Information' },
      description: {
        de: 'Gibt es noch weitere Informationen, die Sie uns mitteilen möchten?',
        en: "Is there any other information you'd like to share with us?",
      },
    },
  ],
  consentField: {
    id: 'agreeWithPersonalDataProcessing',
    type: 'checkbox',
    required: true,
    label: {
      de: 'Ich bin mit der Speicherung und Verarbeitung der hier angegebenen personenbezogenen Daten einverstanden. Die Daten werden allein zum Zwecke der Kontaktaufnahme durch Mitarbeiter der "Private Label Cosmetic Company GmbH" auf Grundlage unserer Datenschutzerklärung erhoben.',
      en: 'I agree to the storage and processing of the personal data given here. The data is collected solely for the purpose of establishing contact by employees of "Private Label Cosmetic Company GmbH" on the basis of our data protection declaration.',
    },
  },
}
